import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ramadanBg from "@/assets/ramadan-bg.jpeg";
import logoWhite from "@/assets/logo-white.png";
import calligraphy from "@/assets/ramadan-calligraphy.svg";

interface RamadanLoaderProps {
  onComplete: () => void;
}

const ScooterIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 64 40" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
    {/* Back wheel */}
    <circle cx="14" cy="32" r="7" stroke="currentColor" strokeWidth="2.5" />
    <circle cx="14" cy="32" r="2" fill="currentColor" />
    {/* Front wheel */}
    <circle cx="50" cy="32" r="7" stroke="currentColor" strokeWidth="2.5" />
    <circle cx="50" cy="32" r="2" fill="currentColor" />
    {/* Body/deck */}
    <path d="M14 32 H50" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    {/* Seat post + seat */}
    <path d="M22 32 L26 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M21 18 H31" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    {/* Handlebar stem */}
    <path d="M44 32 L48 14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    {/* Handlebar */}
    <path d="M43 14 H53" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    {/* Headlight */}
    <circle cx="50" cy="22" r="2" fill="currentColor" opacity="0.7" />
  </svg>
);

const RamadanLoader = ({ onComplete }: RamadanLoaderProps) => {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const duration = 4500;
    const interval = 30;
    const step = (100 / duration) * interval;

    const timer = setInterval(() => {
      setProgress((prev) => {
        const next = prev + step;
        if (next >= 100) {
          clearInterval(timer);
          setTimeout(() => {
            setIsExiting(true);
            setTimeout(onComplete, 600);
          }, 400);
          return 100;
        }
        return next;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [onComplete]);

  return (
    <AnimatePresence>
      {!isExiting && (
        <motion.div
          className="fixed inset-0 z-[9999] flex flex-col items-center justify-center overflow-hidden"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          animate={isExiting ? { opacity: 0 } : { opacity: 1 }}
          transition={{ duration: 0.6, ease: "easeInOut" }}
        >
          {/* Background */}
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${ramadanBg})` }}
          />
          <div className="absolute inset-0 bg-[hsl(192,35%,8%)]/50" />

          {/* Ambient light orbs */}
          {[
            { size: 350, top: '10%', left: '5%', delay: 0, duration: 6 },
            { size: 280, top: '60%', left: '75%', delay: 1.5, duration: 7 },
            { size: 400, top: '30%', left: '50%', delay: 0.8, duration: 8 },
            { size: 250, top: '75%', left: '20%', delay: 2.5, duration: 5.5 },
            { size: 300, top: '15%', left: '80%', delay: 3, duration: 6.5 },
          ].map((orb, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                width: orb.size,
                height: orb.size,
                top: orb.top,
                left: orb.left,
                background: 'radial-gradient(circle, hsla(43,74%,52%,0.2) 0%, hsla(43,74%,52%,0.08) 40%, transparent 70%)',
                filter: `blur(${80 + i * 10}px)`,
              }}
              animate={{ opacity: [0.08, 0.22, 0.08] }}
              transition={{
                duration: orb.duration,
                repeat: Infinity,
                delay: orb.delay,
                ease: "easeInOut",
              }}
            />
          ))}

          {/* Content */}
          <div className="relative z-10 flex flex-col items-center gap-6 px-6">
            {/* Logo */}
            <motion.img
              src={logoWhite}
              alt="SDC Logo"
              className="w-32 md:w-44 drop-shadow-[0_0_30px_hsla(43,74%,52%,0.4)]"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />

            {/* Arabic Calligraphy Font */}
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <img
                src={calligraphy}
                alt="رمضان كريم - Ramadan Kareem"
                className="w-56 md:w-72 mx-auto drop-shadow-[0_0_20px_hsla(43,74%,52%,0.5)]"
                style={{
                  filter: 'brightness(0) saturate(100%) invert(72%) sepia(60%) saturate(500%) hue-rotate(5deg) brightness(105%)',
                }}
              />
              <p className="text-sm md:text-base text-primary/80 tracking-[0.3em] uppercase mt-3">
                Ramadan Kareem
              </p>
            </motion.div>

            {/* Animated scooter loading */}
            <motion.div
              className="w-64 md:w-80 mt-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              <div className="relative">
                {/* Scooter riding along */}
                <motion.div
                  className="absolute -top-10 z-10"
                  style={{ left: `calc(${progress}% - 16px)` }}
                >
                  {/* Scooter bounce */}
                  <motion.div
                    animate={{ y: [0, -3, 0, -1, 0] }}
                    transition={{ duration: 0.6, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <ScooterIcon className="w-8 h-8 text-primary" />
                  </motion.div>
                  {/* Exhaust trail */}
                  <motion.div
                    className="absolute -left-4 top-1/2 -translate-y-1/2"
                    animate={{ opacity: [0, 0.4, 0], scale: [0.5, 1.2, 0.5] }}
                    transition={{ duration: 0.8, repeat: Infinity }}
                  >
                    <div className="w-3 h-3 rounded-full bg-primary/30 blur-sm" />
                  </motion.div>
                </motion.div>

                {/* Track */}
                <div className="h-2 rounded-full bg-secondary/50 backdrop-blur-sm overflow-hidden border border-border/30">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-eco-green via-teal-bright to-primary"
                    style={{ width: `${progress}%` }}
                    transition={{ ease: "linear" }}
                  />
                </div>

                {/* Percentage */}
                <p className="text-center text-xs text-muted-foreground mt-2 tracking-wider">
                  {Math.round(progress)}%
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RamadanLoader;
